// hooks/useCometChatEvents.js
import { useEffect } from 'react';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { CometChatMessageEvents, CometChatGroupEvents, CometChatUIEvents } from '@cometchat/chat-uikit-react';
import { sendChatNotification } from '../services/home.services';
import { trackClickEvent } from '../mixpanel';
import { presenceMap } from '../cometchat';

export const useCometChatEvents = () => {
  useEffect(() => {
    const ccMessageSent = CometChatMessageEvents.ccMessageSent.subscribe(
      async (data) => {
        const receiverType = data.message?.receiverType;
        const receiverId = data.message?.receiver;

        // Track first DM sent - check if conversation has only 1 message
        if (receiverType === "user" && receiverId) {
          try {
            const conversation = await CometChat.getConversation(receiverId, "user");
            if (conversation?.getUnreadMessageCount() === 0) {
              trackClickEvent('chat first message sent', {
                user_id: data?.message?.sender?.uid,
                message_type: data.message?.type,
                conversation_id: data?.message?.conversationId,
                pilot_cohort: true,
                timestamp: new Date().toISOString()

              });
            }
          } catch (error) {
            // Conversation doesn't exist yet, this is the first message
          }
        }

        // Track group post creation
        if (receiverType === "group" && receiverId) {
          trackClickEvent('Post Created in Group', {
            group_id: receiverId,
            group_name: data.message?.receiver?.name,
            message_type: data.message?.type
          });
        }

        try {
          if (receiverType === "user" && receiverId) {
            const user = await CometChat.getUser(receiverId);
            console.log('user chat notif: ', user, receiverId, user.getStatus(), presenceMap[receiverId], presenceMap[receiverId] === "offline");
            if (presenceMap[receiverId] === "offline") {
              await sendChatNotification({
                user_name: user?.name,
                user_id: user?.uid,
                action: 'user_msg',
                msg_sender_name: data?.message?.sender?.name,
                message: data?.message?.text
              });
            }
          } else if (receiverType === "group" && receiverId) {
            const group = await CometChat.getGroup(receiverId);

            const groupMembersRequest = new CometChat.GroupMembersRequestBuilder(receiverId?.guid || receiverId)
              .setLimit(100)
              .build();

            const groupMembers = await groupMembersRequest.fetchNext();

            const offlineMemberIds = groupMembers
              .filter(member => member.getStatus() === "offline")
              .map(member => member.getUid());

            await sendChatNotification({
              action: 'group_msg',
              msg_sender_name: data?.message?.sender?.name,
              message: data?.message?.text,
              discussion_group: data?.message?.receiver?.name,
              user_ids: offlineMemberIds
            });
          }
        } catch (error) {
          console.log("Error in message sent event:", error);
        }
      }
    );

    const ccActiveChatChanged = CometChatUIEvents.ccActiveChatChanged.subscribe(async (data) => {
      if (data && !data.message) {
        const loggedInUser = await CometChat.getLoggedinUser();

        trackClickEvent('chat conversation created', {
          user_id: loggedInUser?.uid,
          pilot_cohort: true,
          recipient_user_id: data?.user?.uid,
          conversation_id: data?.user?.conversationId,
          timestamp: new Date().toISOString()
        });
      }
      console.log("Active chat changed:", data?.message, data?.user);
    });

    const onTextMessageReceived = CometChatMessageEvents.onTextMessageReceived.subscribe(
      async (message) => {
        try {
          const loggedInUser = await CometChat.getLoggedinUser();
          if (message.getReceiverType() === "user" && message.getReceiverId() === loggedInUser?.uid) {
            trackClickEvent('chat reply received', {
              sender_id: message.getSender()?.getUid(),
              sender_name: message.getSender()?.getName(),
              pilot_cohort: true,
              message_type: message.getType(),
              timestamp: new Date().toISOString(),
              conversation_id: message?.conversationId,
            });
          }

          if (message.getReceiverType() === "group" && message.getParentMessageId()) {
            const parentMessage = await CometChat.getMessageDetails(message.getParentMessageId());
            if (parentMessage?.getSender()?.getUid() === loggedInUser?.uid) {
              trackClickEvent('group reply received', {
                group_id: message.getReceiverId(),
                replier_id: message.getSender()?.getUid(),
                replier_name: message.getSender()?.getName(),
                pilot_cohort: true,
                timestamp: new Date().toISOString(),
              });
            }
          }
        } catch (error) {
          console.log("Error tracking message received:", error);
        }
      }
    );

    const onMediaMessageReceived = CometChatMessageEvents.onMediaMessageReceived.subscribe(
      async (message) => {
        try {
          const loggedInUser = await CometChat.getLoggedinUser();
          if (message.getReceiverType() === "user" && message.getReceiverId() === loggedInUser?.uid) {
            trackClickEvent('Reply Received in Conversation', {
              sender_id: message.getSender()?.getUid(),
              sender_name: message.getSender()?.getName(),
              message_type: message.getType()
            });
          }

          // Track when someone replies to user's post in group
          if (message.getReceiverType() === "group" && message.getParentMessageId()) {
            const parentMessage = await CometChat.getMessageDetails(message.getParentMessageId());
            if (parentMessage?.getSender()?.getUid() === loggedInUser?.uid) {
              trackClickEvent('Reply Received on Group Post', {
                group_id: message.getReceiverId(),
                replier_id: message.getSender()?.getUid(),
                replier_name: message.getSender()?.getName(),
                message_type: message.getType()
              });
            }
          }
        } catch (error) {
          console.log("Error tracking message received:", error);
        }
      }
    );

    // Track when user joins a group
    const ccGroupMemberJoined = CometChatGroupEvents.ccGroupMemberJoined.subscribe(
      async (item) => {
        try {
          console.log('item', item);
          const loggedInUser = await CometChat.getLoggedinUser();
          if (item.joinedUser?.getUid() === loggedInUser?.uid) {
            trackClickEvent('group joined', {
              group_id: item.joinedGroup?.guid,
              group_name: item.joinedGroup?.getName(),
              user_id: item.joinedUser?.uid,
              pilot_cohort: true,
              timestamp: new Date().toISOString(),
            });
          }
        } catch (error) {
          console.log("Error tracking group join:", error);
        }
      }
    );

    const ccGroupMemberAdded = CometChatGroupEvents.ccGroupMemberAdded.subscribe(
      async (item) => {
        try {
          const group = item.userAddedIn;
          const addedByUser = item.userAddedBy;
          const addedUsers = item.usersAdded;

          await sendChatNotification({
            action: 'group_add',
            discussion_group: group?.name,
            user_ids: addedUsers?.map(user => user?.uid),
            added_by_id: addedByUser?.uid,
            user_name: addedByUser?.name,
            pilot_cohort: true,
            timestamp: new Date().toISOString(),
          });

        } catch (error) {
          console.log("Error in group member added event:", error);
        }
      }
    );

    return () => {
      ccMessageSent?.unsubscribe();
      ccActiveChatChanged?.unsubscribe();
      onTextMessageReceived?.unsubscribe();
      onMediaMessageReceived?.unsubscribe();
      ccGroupMemberJoined?.unsubscribe();
      ccGroupMemberAdded?.unsubscribe();
    };
  }, []);
};